export interface UserProfile {
  uuid: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  role: 'USER' | 'ADMIN';
  onboarded: boolean;
}

export interface HomeData {
  user: UserProfile;
  stats: { genius_points: number; cowries: number };
  smart_drive: {
    continue_cost: number;
    personal_best_metres: number;
    weekly_best_metres: number;
    week: { from: string; to: string };
  };
}

export interface LaneInput {
  tick: number;
  direction: -1 | 1;
}

export interface RunStart {
  run_uuid: string;
  segment: 0;
  seed: number;
  start_distance_world: number;
  personal_best_metres: number;
  weekly_best_metres: number;
  continue_cost: number;
  week: { from: string; to: string };
  expires_at: string;
}

export interface CrashResult {
  run_uuid: string;
  segment: 0 | 1;
  distance_world: number;
  distance_metres: number;
  near_misses: number;
  personal_best_metres: number;
  weekly_best_metres: number;
  is_weekly_record: boolean;
  can_continue: boolean;
  continue_expires_at: string | null;
  cowries: number;
  continue_cost: number;
}

export interface ContinueResult {
  run_uuid: string;
  segment: 1;
  seed: number;
  start_distance_world: number;
  cowries: number;
}

export type LeaderboardPeriod = 'today' | 'week' | 'all';

export interface LeaderboardEntry {
  rank: number;
  distance_metres: number;
  achieved_at: string;
  user: { uuid: string | null; name: string | null; username?: string | null; avatar_url: string | null };
}

export interface LeaderboardData {
  period: LeaderboardPeriod;
  from: string | null;
  to: string | null;
  entries: LeaderboardEntry[];
  viewer: LeaderboardEntry | null;
}

interface APIResponse<T> {
  status: boolean;
  message?: string;
  code?: string;
  data?: T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  if (UNSAFE.has(method)) {
    const csrf = readCookie('mg_csrf');
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  let response: Response;
  try {
    response = await fetch(`/api${path}`, { ...options, method, credentials: 'include', headers });
  } catch {
    throw new ApiError('Could not reach My Genius. Check your connection and try again.', 0, 'NETWORK');
  }
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new ApiError('The games service is not available just now.', response.status, 'NOT_JSON');
  }
  const body = await response.json() as APIResponse<T>;
  if (!response.ok || body.status === false) {
    throw new ApiError(body.message || 'Request failed', response.status, body.code, body.data);
  }
  return body.data as T;
}

export const api = {
  session: () => request<UserProfile | null>('/account/session'),
  google: (credential: string) => request<{ user: UserProfile }>('/account/google', {
    method: 'POST', body: JSON.stringify({ credential }),
  }),
  logout: () => request<null>('/account/logout', { method: 'POST' }),
  home: () => request<HomeData>('/games/home'),
  startRun: () => request<RunStart>('/games/smart-drive/runs', { method: 'POST', body: '{}' }),
  crash: (runUuid: string, segment: 0 | 1, crashTick: number, inputs: readonly LaneInput[]) =>
    request<CrashResult>(`/games/smart-drive/runs/${encodeURIComponent(runUuid)}/crash`, {
      method: 'POST', body: JSON.stringify({ segment, crash_tick: crashTick, inputs }),
    }),
  continueRun: (runUuid: string) =>
    request<ContinueResult>(`/games/smart-drive/runs/${encodeURIComponent(runUuid)}/continue`, {
      method: 'POST', body: '{}',
    }),
  leaderboard: (period: LeaderboardPeriod) =>
    request<LeaderboardData>(`/games/smart-drive/leaderboard?period=${period}`),
};
